import {
  createAction,
  type Dispatch,
  type ThunkAction,
  type AnyAction,
  type ThunkDispatch,
} from '@reduxjs/toolkit';
import {
  fetchAssortments,
  type ProductCreationDraft,
  setB24ProductsSearchActiveFilters,
} from '@/lib/redux/redux-actions';
import { api } from '@/lib/trpc/client';
import type { RootState } from '@/app/store';
import type { Assortment, AssortmentRich } from '@/server/assortments/assortments';
import type {
  AssortmentProductColorCampaignRole,
  DistributionProfile,
  Event,
  Quarter,
  AssortmentProductRole,
} from '@repo/db';

import type { AppDispatch } from '@/app/store';
import { type AssortmentProduct } from '@/server/assortments/assortment-products';
import type {
  CreateAssortmentProductsResponse,
  ProductCreationData,
} from '@/server/assortments/assortment-product-create';
import type {
  B24Product,
  ProductLookupResponse,
} from '@/server/assortments/assortment-product-lookup';
import type { AssortmentEditData } from '@/server/assortments/assortment-edit';
import { showPrimarySnackbar, showAlertSnackbar } from '@/lib/atoms/notifications';
import { LOCALE_FIN, capitalizeFirstLetter, formatMessage } from '@/lib/helpers';
import type { RouterOutputs } from '@/lib/trpc/react';
import type { AssortmentProductUpdateData } from '@/server/api/routers/assortments';
import type { ProductAddErrorObject } from '@/server/assortments/product-add-error';

export const assortmentRequestStarted = createAction('assortment/request/started');
export const assortmentRequestFailed = createAction<string>('assortment/request/failed');
export const updateAssortmentProductsStatuses = createAction<{
  productsUpdatedData: Record<number, Partial<AssortmentProduct>>;
}>('assortment/products-statuses/update');
export const updateAssortmentProducts = createAction<{
  products?: Record<string, AssortmentProduct>;
  productsValid?: boolean;
}>('assortment/products/update');

export const fetchAssortmentSucceeded = createAction<{
  assortment: AssortmentRich;
}>('assortment/fetch/succeeded');
export const fetchAssortment =
  (assortmentId: string | number): ThunkAction<Promise<void>, RootState, unknown, AnyAction> =>
  (dispatch: ThunkDispatch<RootState, unknown, AnyAction>, getState: () => RootState) => {
    dispatch(assortmentRequestStarted());

    const state = getState();
    const userState = state.user;
    const { activeChain } = userState;

    const b24ProductsSearchState = state.b24ProductsSearch;
    const { activeFilters } = b24ProductsSearchState;

    return api.assortments.getAssortment
      .query({
        id: Number(assortmentId),
        chain: activeChain,
      })
      .then(
        async (data) => {
          const assortment = data;
          dispatch(fetchAssortmentSucceeded({ assortment }));
          dispatch(
            setB24ProductsSearchActiveFilters({
              ...activeFilters,
              brand: assortment.brand,
              productType: assortment.productType || '',
              season: assortment.brand !== 'TEST' ? assortment.season : '',
            })
          );
          await dispatch(fetchAssortmentEvents(Number(assortmentId)));
        },
        ({ message }) => {
          dispatch(assortmentRequestFailed(message));
        }
      );
  };

export const editAssortmentProductSucceeded = createAction<{
  assortment: AssortmentRich;
  message: string;
}>('assortment/product/put/succeeded');
export const editAssortmentProduct =
  (
    productId: string | number,
    assortmentId: string | number,
    updateData: AssortmentProductUpdateData,
    // | AssortmentProductSapInfoEditDialogData
    // | AssortmentProductColorsTableUpdateData,
    cb?: (data: RouterOutputs['assortments']['updateAssortmentProduct']) => void
  ) =>
  (dispatch: Dispatch, getState: () => RootState) => {
    dispatch(assortmentRequestStarted());

    const state = getState();
    const userState = state.user;
    const { activeChain } = userState;

    return api.assortments.updateAssortmentProduct
      .mutate({
        id: Number(productId),
        chain: activeChain,
        assortmentId: Number(assortmentId),
        data: updateData,
      })
      .then(
        (data) => {
          dispatch(editAssortmentProductSucceeded(data));
          if (cb) {
            cb(data);
          }
        },
        ({ message }) => {
          dispatch(assortmentRequestFailed(message));
          showAlertSnackbar({
            message: formatMessage(LOCALE_FIN.EDIT_ASSORTMENT_PRODUCT_FAILED, { message }),
            alertSeverity: 'error',
          });
        }
      );
  };
// Mass edit
export const massEditAssortmentProductsSucceeded = createAction<{ message: string }>(
  'assortment/mass-edit-products/succeeded'
);

export type AssortmentProductMassUpdateData = {
  roles?: {
    productRole?: AssortmentProductRole | null;
  };
  generalItemCategoryGroup?: string;
  plant?: string;
  distributionProfile?: DistributionProfile;
  storageLocation?: string;
  deliveryDate1?: string;
  deliveryDate2?: string;
  deliveryDate3?: string;
  deliveryDate4?: string;
  deliveryDate5?: string;
  deliveryDate6?: string;
  deliveryDate7?: string;
  deliveryDate8?: string;
  campaigns?: string[];
};

export const massEditAssortmentProducts =
  (assortmentId: string | number, updateData: AssortmentProductMassUpdateData, ids: number[]) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch(assortmentRequestStarted());

    return api.assortments.massUpdateAssortmentProducts
      .mutate({
        ids,
        data: updateData,
      })
      .then(
        async (data) => {
          dispatch(massEditAssortmentProductsSucceeded(data));
          await dispatch(fetchAssortment(assortmentId));
          showAlertSnackbar({
            message:
              ids.length === 1
                ? LOCALE_FIN.PRODUCT_WAS_UPDATED_SUCCESSFULLY
                : `${ids.length} ${LOCALE_FIN.PRODUCTS_WERE_UPDATED_SUCCESSFULLY}.`,
            alertSeverity: 'success',
          });
        },
        ({ message }) => {
          dispatch(assortmentRequestFailed(message));
          showAlertSnackbar({
            message: `${LOCALE_FIN.PRODUCT_MASS_EDIT_FAILED}: ${message}`,
            alertSeverity: 'error',
          });
        }
      );
  };

type AssortmentProductPriceMassUpdateData = {
  discountPercent: number;
  priceType: 'sapNetPrice' | 'purchase';
};

export const massEditAssortmentProductPrices =
  (assortmentId: number, updateData: AssortmentProductPriceMassUpdateData, ids: number[]) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch(assortmentRequestStarted());

    const priceName =
      updateData.priceType === 'sapNetPrice'
        ? LOCALE_FIN['MASS_SAP_NET_PRICE']
        : LOCALE_FIN['PURCHASE_PRICE'];
    const priceNameGenitive =
      updateData.priceType === 'sapNetPrice'
        ? LOCALE_FIN['MASS_SAP_NET_PRICE_S']
        : LOCALE_FIN['PURCHASE_PRICE_S'];

    return api.assortments.massUpdateAssortmentProductPrices
      .mutate({
        ids,
        data: updateData,
      })
      .then(
        async (data) => {
          dispatch(massEditAssortmentProductsSucceeded(data));
          await dispatch(fetchAssortment(assortmentId));

          if (data.count > 0) {
            showAlertSnackbar({
              message: `${capitalizeFirstLetter(priceName)} ${LOCALE_FIN['WAS_UPDATED_SUCCESSFULLY']} ${data.count} ${LOCALE_FIN['FOR_PRODUCT']}.`,
              alertSeverity: 'success',
            });
          } else {
            showAlertSnackbar({
              message: `${capitalizeFirstLetter(priceNameGenitive)} ${LOCALE_FIN['UPDATE_FAILED'].toLocaleLowerCase()}.`,
              alertSeverity: 'error',
            });
          }
        },
        ({ message }) => {
          dispatch(assortmentRequestFailed(message));

          showAlertSnackbar({
            message: `${capitalizeFirstLetter(priceNameGenitive)} ${LOCALE_FIN['UPDATE_FAILED'].toLocaleLowerCase()}.`,
            alertSeverity: 'error',
          });
        }
      );
  };

export const postAssortmentSucceeded = createAction('assortment/post/succeeded');
export const postAssortment =
  (assortment: Assortment, setId: (id: number | null) => void) =>
  (dispatch: Dispatch, getState: () => RootState) => {
    dispatch(assortmentRequestStarted());

    const state = getState();
    const userState = state.user;
    const { activeChain, user } = userState;

    return api.assortment.postAssortment
      .mutate({
        ...assortment,
        type: assortment.type,
        quarter: assortment.quarter ?? null,
        chain: activeChain,
        productType: assortment.productType || '',
        brandRep: parseInt(assortment.brandRep),
        assortmentLead: parseInt(assortment.assortmentLead),
        taxClass: assortment.taxClass!,
        nosType: assortment.nosType || null,
        startDate: assortment.startDate || null,
        endDate: assortment.endDate || null,
        size: assortment.size || null,
      })
      .then(
        (data: {
          status: string;
          result: {
            assortment: {
              id: number | null;
            };
          };
        }) => {
          setId(data.result.assortment.id);
          dispatch(postAssortmentSucceeded());
          dispatch(fetchAssortments() as any);
        },
        ({ message }) => {
          dispatch(assortmentRequestFailed(message));
          showPrimarySnackbar({
            message: `Failed to create assortment: ${message}`,
          });
        }
      );
  };

export const editAssortmentSucceeded = createAction<Record<string, any>>(
  'assortment/put/succeeded'
);
export const editAssortment =
  (id: number, updateData: Partial<AssortmentEditData>, sapIds?: string[], productIds?: number[]) =>
  (dispatch: Dispatch) => {
    dispatch(assortmentRequestStarted());

    return api.assortment.editAssortment
      .mutate({
        id,
        updateData,
        sapIds,
        productIds,
      })
      .then(
        ({ assortment, productsUpdatedData }) => {
          if (assortment) {
            dispatch(editAssortmentSucceeded({ assortment }));
          }
          if (productsUpdatedData) {
            dispatch(
              updateAssortmentProductsStatuses({
                productsUpdatedData,
              })
            );
          }
        },
        ({ message }) => {
          dispatch(assortmentRequestFailed(message));
          showPrimarySnackbar({
            message: `Failed to edit assortment: ${message}`,
          });
        }
      );
  };

export const deleteAssortmentsSucceeded = createAction('assortments/delete/succeeded');
export const deleteAssortments =
  (ids: number[], cb?: (ids: number[]) => void) => (dispatch: Dispatch) => {
    dispatch(assortmentRequestStarted());
    return api.assortment.deleteAssortments
      .mutate({
        ids,
      })
      .then(
        () => {
          dispatch(deleteAssortmentsSucceeded());
          dispatch(fetchAssortments() as any);
          cb?.(ids);
        },
        ({ message }) => {
          dispatch(assortmentRequestFailed(message));
          showPrimarySnackbar({
            message: `Failed to delete assortment: ${message}`,
          });
        }
      );
  };

export const postAssortmentMessageSucceeded = createAction('assortment/messages/post/succeeded');
export const postAssortmentMessage =
  (
    assortmentId: number,
    requestData: {
      message: string;
      messagePlainText: string;
      mentions: string[];
      assortmentName: string;
    }
  ) =>
  (dispatch: Dispatch, getState: () => RootState) => {
    dispatch(assortmentRequestStarted());

    const state = getState();
    const userState = state.user;
    const { activeChain } = userState;

    return api.assortment.createAssortmentMessage
      .mutate({
        ...requestData,
        assortmentId,
        chain: activeChain,
      })
      .then(
        (assortment) => {
          dispatch(postAssortmentMessageSucceeded());
          if (assortment) {
            dispatch(fetchAssortmentSucceeded({ assortment }));
          }
        },
        ({ message }) => {
          dispatch(assortmentRequestFailed(message));
          showPrimarySnackbar({
            message: `Failed to post assortment message: ${message}`,
          });
        }
      );
  };

export const deleteAssortmentMessageSucceeded = createAction(
  'assortment/messages/delete/succeeded'
);
export const deleteAssortmentMessage =
  (messageId: number, assortmentId: number) => (dispatch: Dispatch) => {
    dispatch(assortmentRequestStarted());

    return api.assortment.deleteAssortmentMessage
      .mutate({
        id: messageId,
        assortmentId,
      })
      .then(
        (assortment) => {
          dispatch(deleteAssortmentMessageSucceeded());
          if (assortment) {
            dispatch(fetchAssortmentSucceeded({ assortment }));
          }
        },
        ({ message }) => {
          dispatch(assortmentRequestFailed(message));
          showPrimarySnackbar({
            message: `Failed to delete assortment message: ${message}`,
          });
        }
      );
  };

export const editAssortmentProductsOrderSucceeded = createAction(
  'assortment/products-order/put/succeeded'
);
export const editAssortmentProductsOrder =
  (
    items: { assortmentId: number; productCode: string; orderNumber: number }[],
    assortmentId: number
  ) =>
  (dispatch: Dispatch) => {
    dispatch(assortmentRequestStarted());

    return api.assortment.updateAssortmentProductsOrder
      .mutate({
        items,
        assortmentId,
      })
      .then(
        (assortment) => {
          dispatch(editAssortmentProductsOrderSucceeded());
          if (assortment) {
            dispatch(fetchAssortmentSucceeded({ assortment }));
          }
        },
        ({ message }) => {
          dispatch(assortmentRequestFailed(message));
          showPrimarySnackbar({
            message: `Failed to update products order: ${message}`,
          });
        }
      );
  };

export const postAssortmentProductsSucceeded = createAction('assortment/product/post/succeeded');
export const postAssortmentProducts =
  (
    assortmentId: number,
    products: ProductCreationData[],
    cb: (error: boolean, data?: CreateAssortmentProductsResponse) => void
  ) =>
  (dispatch: AppDispatch) => {
    dispatch(assortmentRequestStarted());
    return api.assortments.createAssortmentProducts
      .mutate({
        assortmentId,
        products,
      })
      .then(
        (data) => {
          if (data.success) {
            dispatch(postAssortmentProductsSucceeded());
            cb(false);
          } else {
            dispatch(assortmentRequestFailed(''));
            cb(true, data);
          }
        },
        ({ message }) => {
          // this one has it's own snackbar for messages
          dispatch(assortmentRequestFailed(message));
          cb(true);
        }
      );
  };

export const deleteAssortmentProductSucceeded = createAction('assortment/product/delete/succeeded');
export const deleteAssortmentProduct =
  (id: number, assortmentId: number, colorIds: number[]) =>
  (dispatch: Dispatch, getState: () => RootState) => {
    dispatch(assortmentRequestStarted());

    const state = getState();
    const userState = state.user;
    const { activeChain } = userState;

    return api.assortments.deleteAssortmentProduct
      .mutate({
        id,
        assortmentId,
      })
      .then(
        (assortment) => {
          dispatch(deleteAssortmentProductSucceeded());
          if (assortment) {
            dispatch(fetchAssortmentSucceeded({ assortment }));
          }
        },
        ({ message }) => {
          dispatch(assortmentRequestFailed(message));
          showPrimarySnackbar({
            message: `Failed to delete assortment product: ${message}`,
          });
        }
      );
  };

export const refetchAssortmentProductSapSucceeded = createAction(
  'assortment/product/sap/refetch/succeeded'
);

export const refetchAssortmentProductSap =
  (
    assortmentId: number,
    kwNumber: string,
    cb: (response: { success: boolean; error: string }) => void
  ) =>
  (dispatch: Dispatch) => {
    dispatch(assortmentRequestStarted());
    return api.assortments.refetchAssortmentProduct
      .query({
        assortmentId,
        kwNumber,
      })
      .then(
        ({ assortment, status, error }) => {
          if (status.toString().startsWith('4')) {
            dispatch(assortmentRequestFailed(''));
            cb({ success: false, error });
          } else {
            dispatch(refetchAssortmentProductSapSucceeded());
            cb({ success: true, error });
            if (assortment) {
              dispatch(fetchAssortmentSucceeded({ assortment }));
            }
          }
        },
        ({ message }) => {
          dispatch(assortmentRequestFailed(message));
          showPrimarySnackbar({
            message: `Failed to refetch SAP ids: ${message}`,
          });
        }
      );
  };

export const assortmentEventsFetchSucceeded = createAction<{ events: Event[] }>(
  'assortment/events/fetch/succeeded'
);
export const fetchAssortmentEvents = (assortmentId: number) => (dispatch: Dispatch) => {
  dispatch(assortmentRequestStarted());
  return api.assortment.getAssortmentProductEvents
    .query({
      assortmentId,
    })
    .then(
      (events) => {
        if (events) {
          dispatch(assortmentEventsFetchSucceeded({ events }));
        }
      },
      ({ message }) => {
        dispatch(assortmentRequestFailed(message));
      }
    );
};

// SAP UPDATE
export const updateAssortmentProductSapSucceeded = createAction(
  'assortment/product/sap/update/succeeded'
);
export const updateAssortmentProductSap =
  (
    productId: number,
    assortmentId: number,
    cypress: boolean,
    directRequest: boolean,
    cb?: (res: SapRequestResponse) => void
  ) =>
  (dispatch: AppDispatch) => {
    dispatch(assortmentRequestStarted());

    return api.assortments.requestSapActions
      .mutate({
        items: [{ productId, action: 'update' }],
      })
      .then(
        async (data) => {
          dispatch(updateAssortmentProductSapSucceeded());
          if (data.productsUpdatedData) {
            // Lock products that are being updated
            dispatch(
              updateAssortmentProductsStatuses({
                productsUpdatedData: data.productsUpdatedData,
              })
            );
          } else {
            await dispatch(fetchAssortment(assortmentId.toString()));
          }
          cb?.(data);
        },
        ({ message }) => {
          dispatch(assortmentRequestFailed(message));
          showPrimarySnackbar({
            message: `Failed to request SAP update: ${message}`,
          });
        }
      );
  };

export const createAssortmentProductSapSucceeded = createAction(
  'assortment/product/sap/create/succeeded'
);

export type SapRequestResponse = RouterOutputs['assortments']['requestSapActions'];
export const createAssortmentProductSap =
  (
    productId: number,
    assortmentId: number,
    directRequest: boolean,
    cb?: (res: SapRequestResponse) => void
  ) =>
  (dispatch: AppDispatch) => {
    dispatch(assortmentRequestStarted());
    return api.assortments.requestSapActions
      .mutate({
        items: [{ productId, action: 'create' }],
      })
      .then(
        async (data) => {
          dispatch(createAssortmentProductSapSucceeded());
          if (data?.productsUpdatedData) {
            // Lock products that are being updated
            dispatch(
              updateAssortmentProductsStatuses({
                productsUpdatedData: data.productsUpdatedData,
              })
            );
          } else {
            await dispatch(fetchAssortment(assortmentId.toString()));
          }
          cb?.(data);
        },
        ({ message }) => {
          dispatch(assortmentRequestFailed(message));
          showPrimarySnackbar({
            message: `Failed to request SAP create: ${message}`,
          });
        }
      );
  };

/**
 * Mass update / create products in SAP
 */
export const massUpdateProductsSucceeded = createAction(
  'assortment/mass-update-products/succeeded'
);
export const massUpdateCreateSapProducts =
  (actionsData: Record<number, 'update' | 'create'>, assortmentId: number, cb: () => void) =>
  (dispatch: AppDispatch) => {
    dispatch(assortmentRequestStarted());

    return api.assortments.requestSapActions
      .mutate({
        items: Object.entries(actionsData).map(([productId, action]) => ({
          productId: Number(productId),
          action,
        })),
      })
      .then(
        (data) => {
          dispatch(massUpdateProductsSucceeded());
          // Lock products that are being updated
          const productsUpdatedData = data?.productsUpdatedData || {};
          dispatch(updateAssortmentProductsStatuses({ productsUpdatedData }));
          cb();
        },
        ({ message }) => {
          dispatch(assortmentRequestFailed(message));
          showPrimarySnackbar({
            message: `Failed to request SAP mass update: ${message}`,
          });
        }
      );
  };

/**
 * Mass update Pricat products
 */
export const massUpdateProductPricatData =
  (productIds: number[], assortmentId: number, cb: () => void) => (dispatch: AppDispatch) => {
    dispatch(assortmentRequestStarted());

    return api.assortment.massUpdateProductPricatData
      .mutate({
        assortmentId,
        productIds,
      })
      .then(
        (data) => {
          dispatch(massUpdateProductsSucceeded());
          // Lock products that are being updated
          const productsUpdatedData = data?.productsUpdatedData || {};
          dispatch(updateAssortmentProductsStatuses({ productsUpdatedData }));

          if (typeof cb === 'function') {
            cb();
          }
        },
        ({ message }) => {
          dispatch(assortmentRequestFailed(message));
          showPrimarySnackbar({
            message: `Failed to request SAP mass update: ${message}`,
          });
        }
      );
  };

export const assortmentProductsLookupSucceeded = createAction(
  'assortment/product_lookup/post/succeeded'
);
export const assortmentProductsLookup =
  (
    assortmentId: number,
    products: B24Product[],
    addData: { products: ProductCreationDraft[] },
    combined: boolean,
    cb: (
      error: boolean,
      responseData?: ProductLookupResponse,
      addData?: { products: ProductCreationDraft[] } | null,
      combined?: boolean,
      msg?: string | null
    ) => void
  ) =>
  (dispatch: AppDispatch) => {
    dispatch(assortmentRequestStarted());

    return api.assortments.assortmentProductsLookup
      .mutate({
        assortmentId,
        products,
        combined,
      })
      .then(
        (data) => {
          if (!data.success) {
            dispatch(assortmentRequestFailed(data.message ?? ''));
            cb(true, data, null, combined, data.message);
          } else {
            dispatch(assortmentProductsLookupSucceeded());
            cb(false, data, addData, combined);
          }
        },
        ({ message }) => {
          dispatch(assortmentRequestFailed(message));
          showPrimarySnackbar({
            message: `Failed to lookup products: ${message}`,
          });
          cb(true);
        }
      );
  };
